import React, { useState, useEffect, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { MdNotifications, MdSettings, MdLogout, MdPerson, MdKeyboardArrowDown, MdMenu } from 'react-icons/md';

const LiveClock = memo(function LiveClock() {
  const [time, setTime] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const timeStr = time.toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', second:'2-digit' });
  return (
    <div className="topbar-clock">
      🕑 {timeStr}
    </div>
  );
});

export default function Topbar({ collapsed, isMobile, onMenuOpen }) {
  const { currentUser, unreadCount, logout } = useApp();
  const navigate = useNavigate();
  const dateStr = new Date().toLocaleDateString('en-IN', { weekday:'long', month:'short', day:'numeric', year:'numeric' });

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    const onEsc   = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onEsc); };
  }, [menuOpen]);

  return (
    <header className={`app-topbar${isMobile ? ' is-mobile' : ''}${collapsed && !isMobile ? ' is-collapsed' : ''}`}>

      <div className="app-topbar__left">
        {isMobile && (
          <button type="button" className="app-topbar__menu-btn" onClick={onMenuOpen} aria-label="Open menu">
            <MdMenu />
          </button>
        )}
        <div className="app-topbar__hospital">
          <div className="app-topbar__hospital-name">
            {currentUser?.hospital || 'HCEN Hospital'}
          </div>
          <div className="app-topbar__hospital-date">{dateStr}</div>
        </div>
      </div>

      <div className="app-topbar__center">
        <LiveClock />
      </div>

      <div className="app-topbar__right">
        <button
          type="button"
          className="app-topbar__icon-btn"
          onClick={() => navigate('/notifications')}
          aria-label="Notifications"
        >
          <MdNotifications style={{ fontSize: 20 }} />
          {unreadCount > 0 && (
            <span className="app-topbar__notif-badge">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        <div ref={menuRef} className="app-topbar__user-wrap">
          <button
            type="button"
            className={`app-topbar__user-btn${menuOpen ? ' is-open' : ''}`}
            onClick={() => setMenuOpen(p => !p)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <div className="app-topbar__avatar">
              {currentUser?.name?.charAt(0)?.toUpperCase() || 'H'}
            </div>
            <div className="app-topbar__user-text">
              <span className="app-topbar__user-name">{currentUser?.name?.split(' ').slice(0, 2).join(' ') || 'Hospital'}</span>
              <span className="app-topbar__user-role">{currentUser?.role || 'Hospital'}</span>
            </div>
            <MdKeyboardArrowDown className={`app-topbar__chevron${menuOpen ? ' is-open' : ''}`} />
          </button>

          {menuOpen && (
            <div role="menu" className="app-topbar__dropdown">
              <div className="app-topbar__dropdown-head">
                <div className="app-topbar__dropdown-title">
                  {currentUser?.hospital || currentUser?.name || 'Hospital'}
                </div>
                <div className="app-topbar__dropdown-email">
                  {currentUser?.email || ''}
                </div>
              </div>
              <MenuItem icon={<MdPerson />} label="My Profile" onClick={() => { setMenuOpen(false); navigate('/settings'); }} />
              <MenuItem icon={<MdSettings />} label="Settings" onClick={() => { setMenuOpen(false); navigate('/settings'); }} />
              <div className="app-topbar__dropdown-divider" />
              <MenuItem icon={<MdLogout />} label="Logout" danger onClick={() => { setMenuOpen(false); logout(); navigate('/login'); }} />
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function MenuItem({ icon, label, onClick, danger }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`app-topbar__menu-item${danger ? ' is-danger' : ''}`}
    >
      <span className="app-topbar__menu-item-icon">{icon}</span>
      <span>{label}</span>
    </button>
  );
}
